/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { format } from 'date-fns';
import { CalendarDays, SearchX, Trophy } from 'lucide-react';
import React from 'react';
import { getDateLocale, i18n } from 'shared/i18n';
import UDMarqueeText from 'ud-ui/components/marquee-text';
import UDText from 'ud-ui/components/text';
import IconBadge, { AnalyticsTone, TONE_COLORS } from './icon-badge';

interface RecordCardShellProps {
  icon: React.ReactNode;
  title: string;
  tone?: AnalyticsTone;
  /** Сам рекорд — крупно под шапкой: подпись («Голов») над значением («14»). */
  metric?: { label: string; value: React.ReactNode };
  /** Нет данных за период — вместо тела рисуется пустое состояние. */
  isEmpty?: boolean;
  /** Подвал: соревнование и дата матча. */
  championshipName?: string;
  matchDate?: string;
  className?: string;
  children?: React.ReactNode;
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.SurfaceCard};
  border: 1px solid ${({ theme }) => theme.colors.LineSubtle};
  border-radius: ${({ theme }) => theme.radii.panel};
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.elevation.low};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px 12px;
`;

/** Тело: рекорд, подробности под ним — с одним шагом между блоками; растягивается, чтобы подвалы в ряду совпали. */
const Body = styled.div`
  flex: 1;
  padding: 0 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/** Одна строка без переноса: высота подвала не зависит от ширины и длины названия. */
const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 12px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.LineSubtle};
  background: ${({ theme }) => theme.colors.SurfaceSunken};
`;

const Meta = styled.span<{ $grow?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  /* Турнир забирает остаток строки и обрезается; дата не сжимается. */
  flex: ${({ $grow }) => ($grow ? '1 1 0' : '0 0 auto')};
  color: ${({ theme }) => theme.colors.InkSecondary};

  svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.InkMuted};
  }
`;

/** Рекорд на подложке цвета карточки: главное в карточке читается первым, а не прячется в уголок шапки. */
const MetricBlock = styled.div<{ $tone: string }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px;
  border-radius: ${({ theme }) => theme.radii.card};
  background: color-mix(in srgb, ${({ $tone }) => $tone} 14%, transparent);
`;

const EmptyBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 0 8px;
  margin: auto 0;
  color: ${({ theme }) => theme.colors.InkMuted};
`;

/**
 * Общая рама карточек рекордов: шапка (значок и название), сам рекорд крупно, подробности (тело), подвал с
 * турниром и датой. Отдельные рекорды различаются только телом.
 */
const RecordCardShell = ({
  icon,
  title,
  tone = 'gold',
  metric,
  isEmpty,
  championshipName,
  matchDate,
  className,
  children,
}: RecordCardShellProps) => {
  return (
    <Card className={className}>
      <Header>
        <IconBadge icon={icon} tone={tone} size={40} />
        <UDText variant="body-1" as="div" className="!text-inkPrimary">
          {title}
        </UDText>
      </Header>

      <Body>
        {isEmpty ? (
          <EmptyBlock>
            <SearchX size={32} />
            <UDText variant="body-2" as="div">
              <Trans>Нет данных за выбранный период</Trans>
            </UDText>
          </EmptyBlock>
        ) : (
          <>
            {metric && (
              <MetricBlock $tone={TONE_COLORS[tone]} data-testid="record-metric">
                <UDText variant="body-2" as="div" className="!text-inkSecondary">
                  {metric.label}
                </UDText>
                <UDText variant="title-1" as="div" className="!text-inkPrimary leading-none">
                  {metric.value}
                </UDText>
              </MetricBlock>
            )}
            {children}
          </>
        )}
      </Body>

      {!isEmpty && (championshipName || matchDate) && (
        <Footer>
          {championshipName && (
            <Meta $grow>
              <Trophy size={14} />
              <UDMarqueeText variant="text-2" className="flex-1">
                {championshipName}
              </UDMarqueeText>
            </Meta>
          )}
          {matchDate && (
            <Meta>
              <CalendarDays size={14} />
              <UDText variant="text-2-bold" as="span" className="whitespace-nowrap">
                {format(new Date(matchDate), 'd MMM yyyy', { locale: getDateLocale(i18n.locale) })}
              </UDText>
            </Meta>
          )}
        </Footer>
      )}
    </Card>
  );
};

export default RecordCardShell;
